import { Moment } from 'moment';
import {
  EOnboardingSubStep,
  EOnboardingStepUI,
} from 'pages/public/Onboarding_Lite/OnboardingStepper/OnboardingStepper.types';
import {
  TLeasePacketPropertyUi,
  TLeasePacketUnitUi,
  TSignature,
  TSignatureUI,
} from './application.types';
import {
  EAnimalStatusUponDeletionUI,
  EAnimalTypes,
  EAnimalTypesUI,
  TAnimalApplicationDataDB,
  TAnimalApplicationDataUI,
  TAnimalConversionToPetUI,
  TAnimalIncompleteUI,
  TAnimalWeightProps,
  TBlockedActionUI,
} from './animal.types';
import {
  EAnimalStatusesUi,
  EAnimalSingleStatusDB,
  EApplicationStatuses,
  EApplicationStatusesUi,
  EGenderUi,
  EReproductive,
  EVerificationStatusUi,
  TPredefinedOptionType,
  TPredefinedOptionTypeUi,
} from './generalTypes';
import {
  ETenantResidencyStatus,
  ETenantResidencyStatusUI,
} from './tenant.types';

export type TExistingAnimalsDB = {
  animalId: string;
  applicationId: string | null;
  name: string;
  type: EAnimalTypes;
  animalSpecies: TPredefinedOptionType;
  selected: boolean;
  moveInDate?: string;
  hasApplication?: boolean;
  animalStatus?: EApplicationStatuses | null;
  singleStatus?: EAnimalSingleStatusDB | null;
};

export enum EOnboardingStepDB {
  RESIDENCY_FORM_CREATE = 'RESIDENCY_FORM_CREATE',
  RESIDENCY_FORM_COMPLETE = 'RESIDENCY_FORM_COMPLETE',
  RESIDENCY_ANIMAL_COUNT = 'RESIDENCY_ANIMAL_COUNT',
  RESIDENCY_ANIMAL_CREATE = 'RESIDENCY_ANIMAL_CREATE',
  RESIDENCY_ANIMAL_COMPLETE = 'RESIDENCY_ANIMAL_COMPLETE',
  PAYMENT = 'PAYMENT',
}

export type TResidencyData = {
  id: string;
  leaseToDate: string;
  leaseNextDate?: string;
  moveInDate: string;
  propertyManagementLogo: string;
  propertyManagementName: string;
  propertyName: string;
  unitNumber: string;
  hasAnimal: boolean;
  animals: TAnimalResidencyDataDB[];
  existingAnimals: TExistingAnimalsDB[];
  propertyManagementId: string;
  applicantAnimals: TApplicantAnimalsDB[];
  notManagedAnimals: TNotManagedAnimalDB[];
  policyId: string | null;
  propertyId: string;
  onboardingStateId: string;
  resident: boolean | null;
  animalCount: number | null;
  roommateAnimalCount: number | null;
  roommateHasAnimal: boolean | null;
  step: EOnboardingStepDB;
  isPaymentOn: boolean;
  signature: TSignature | null;
  shouldTenantSignAddendum: boolean;
};

export type TResidencyDataUI = {
  id: string;
  leaseToDate?: string;
  moveInDate?: string;
  leaseNextDate?: string;
  propertyManagementLogo: string;
  propertyManagementName: string;
  propertyName: string;
  unitNumber: string;
  hasAnimal?: boolean;
  animalResidencies: TAnimalResidencyDataUI[];
  existingAnimals: TAnimalIncompleteUI[];
  propertyManagementId: string;
  applicantAnimals: TApplicantAnimalsUI[];
  notManagedAnimals: TNotManagedAnimalUI[];
  policyId: string | null;
  propertyId: string;
  onboardingStateId: string;
  status: EApplicationTypeUI | null;
  animalCount: number | null;
  roommateAnimalCount: number | null;
  roommateHasAnimal: boolean | null;
  step: EOnboardingStepUI;
  subStep?: EOnboardingSubStep;
  isPaymentOn: boolean;
  addendumSignature: TSignatureUI | null;
  shouldTenantSignAddendum: boolean;
};

export type TCompleteAnimalDetialDB = {
  animalId: string;
  moveIn?: string;
  reason?: string;
};

export enum EApplicationTypeDB {
  RESIDENT = 'RESIDENT',
  APPLICANT = 'APPLICANT',
}

export enum EApplicationTypeUI {
  RESIDENT = 'Resident',
  APPLICANT = 'Applicant',
}

export type TCreateTenantResidencyDB = {
  state: EApplicationTypeDB;
  accessCodeId: string;
  propertyName: string;
  propertyId: string | null;
  unitName: string;
  moveIn: string;
  leaseTo: string;
  hasAnimal?: boolean;
  nextLeaseTo?: string;
  unitId?: string;
  roommateHasAnimal?: boolean;
  animalDetails: TCompleteAnimalDetialDB[] | null;
  animalCount?: number;
  roommateAnimalCount?: number;
  signature?: TSignature;
};

export type TCreateTenantResidencyUI = {
  state: EApplicationTypeUI;
  accessCodeId: string;
  propertyName: { label: string; value: TLeasePacketPropertyUi };
  unit: { label: string; value: TLeasePacketUnitUi };
  startTime: Moment;
  endTime: Moment;
  secondEndTime?: Moment;
  haveAnimal?: boolean;
  roommateHasAnimal?: boolean;
  animals?: TAnimalIncompleteUI[];
  existingAnimals?: TAnimalIncompleteUI[];
  animalCount?: number;
  roommateAnimalCount?: number;
  signature?: TSignatureUI;
};

export type TCompleteTenantResidencyUI = {
  residencyId: string;
  haveAnimal: boolean;
  roommateHasAnimal: boolean;
  animals: TAnimalIncompleteUI[];
  existingAnimalList: TAnimalIncompleteUI[];
  unitNumber?: string;
  leaseToDate?: string;
  moveInDate?: string;
  applicantAnimals: TApplicantAnimalsUI[];
};
export type TCompleteTenantResidencyDB = {
  residencyId: string;
  hasAnimal: boolean;
  roommateHasAnimal: boolean;
  animalDetails: TCompleteAnimalDetialDB[];
  unitNumber?: string;
  leaseTo?: string;
  moveIn?: string;
};

export enum EAnimalResidencyStatusUI {
  ACTIVE = 'Moved In',
  TERMINATED = 'Moved out',
}

export enum EAnimalResidencyStatusDB {
  ACTIVE = 'ACTIVE',
  TERMINATED = 'TERMINATED',
}

export enum EResidencyStatusUI {
  RESIDENT = 'Resident',
  APPLICANT = 'Applicant',
  FORMER_RESIDENT = 'Former resident',
  DENIED = 'Not Approved',
}

export enum EResidencyStatusDB {
  RESIDENT = 'RESIDENT',
  APPLICANT = 'APPLICANT',
  FORMER_RESIDENT = 'FORMER_RESIDENT',
  DENIED = 'DENIED',
}

export type TAnimalResidencyDataUI = {
  id: string;
  name: string;
  status: EAnimalResidencyStatusUI;
  displayStatus?: EApplicationStatusesUi | null;
  type: EAnimalTypesUI;
  species: TPredefinedOptionTypeUi;
  typeConfirmed: boolean;
  moveInDate?: string;
};

export type TAnimalResidencyDataDB = {
  animalId: string;
  name: string;
  status: EAnimalResidencyStatusDB;
  singleStatus?: EAnimalSingleStatusDB | null;
  type: EAnimalTypes;
  animalSpecies: TPredefinedOptionType;
  typeConfirmed: boolean;
  moveInDate?: string;
};

export type TValidateResidencyUnitUI = {
  unitNumber: string;
  residencyApplicationId: string;
  propertyName: string;
};

export type TChangeResidencyFormDataDB = {
  id: string;
  unitNumber: string;
  moveInDate: string;
  leaseToDate: string;
  propertyName?: string;
};
export type TChangeResidencyFormDataUI = {
  propertyName?: string;
  id: string;
  unitNumber: { value?: string; label: string };
  moveInDate: Moment;
  leaseToDate: Moment;
};

export type TApplicantAnimalsDB = {
  animalId: string;
  name: string;
  type: EAnimalTypes;
  animalSpecies: TPredefinedOptionType;
  status: EApplicationStatuses;
  singleStatus?: EAnimalSingleStatusDB | null;
  applicationId: string;
  typeConfirmed?: boolean;
  moveInDate?: string;
};
export type TApplicantAnimalsUI = {
  id: string;
  name: string;
  type: EAnimalTypesUI;
  species: TPredefinedOptionTypeUi;
  status: EApplicationStatusesUi;
  displayStatus?: EApplicationStatusesUi | null;
  applicationId: string;
  typeConfirmed?: boolean;
  moveInDate?: string;
};

// PP-10057: animals whose application OPP retracted because the property does not manage the type.
// A list of their own so the onboarding payment step never sees them.
export type TNotManagedAnimalDB = {
  animalId: string;
  applicationId: string;
  name: string;
  type: EAnimalTypes;
  animalSpecies: TPredefinedOptionType;
  typeConfirmed?: boolean;
  moveInDate?: string;
  retractionMessage: string | null;
};

export type TNotManagedAnimalUI = {
  id: string;
  applicationId: string;
  name: string;
  type: EAnimalTypesUI;
  species: TPredefinedOptionTypeUi;
  typeConfirmed?: boolean;
  moveInDate?: string;
  retractionMessage: string | null;
};

export type TResidencyApplicationDataDB = {
  id: string;
  leaseToDate: string;
  moveInDate: string;
  propertyManagementLogo: string;
  propertyManagementName: string;
  propertyName: string;
  unitNumber: string;
  hasAnimal?: boolean;
  applicantAnimals: TApplicantAnimalsDB[];
  existingAnimals: TExistingAnimalsDB[];
  propertyManagementId: string;
  roommateHasAnimal: boolean;
};

export type TResidencyApplicationDataUI = {
  id: string;
  leaseToDate: string;
  moveInDate: string;
  propertyManagementLogo: string;
  propertyManagementName: string;
  propertyName: string;
  unitNumber: string;
  hasAnimal?: boolean;
  applicantAnimals: TApplicantAnimalsUI[];
  existingAnimals: TAnimalIncompleteUI[];
  propertyManagementId: string;
  roommateHasAnimal: boolean;
};

export type TCompleteResidencyApplicationDB = {
  residencyApplicationId: string;
  hasAnimal: boolean;
  roommateHasAnimal: boolean;
  animalIds: string[];
};
export type TCompleteResidencyApplicationUI = {
  residencyApplicationId: string;
  unitNumber?: string;
  moveInDate?: string;
  leaseToDate?: string;
  haveAnimal: boolean;
  roommateHasAnimal: boolean;
  animals: TAnimalIncompleteUI[];
  existingAnimals: TAnimalIncompleteUI[];
};

export type TSetApplicantsMoveInDateDB = {
  applicationId: string;
  moveInDate: string;
};

export type TSetApplicantsMoveInDateUI = {
  id: string;
  moveInDate: Moment;
};

export type TResidencyAnimalCountUI = {
  residencyId: string;
  animalCount: number | null;
  animalCountChangeReason?: string;
  roommateAnimalCount: number | null;
  roommateAnimalCountChangeReason?: string;
};
export type TResidencyAnimalCountDB = {
  residencyId: string;
  animalCount: number;
  animalCountChangeReason?: string;
  roommateAnimalCount: number;
  roommateAnimalCountChangeReason?: string;
};

export type TResidencyAnimalInfoDB = {
  id: string;
  breed?: TPredefinedOptionType;
  name: string;
  profilePicture?: string;
  type: EAnimalTypes;
  specie?: TPredefinedOptionType;
  complete: boolean;

  // Add more fields when BE returned more data to escape using animalsList api
};

export type TResidencyAnimalInfoUI = {
  id: string;
  breed?: TPredefinedOptionTypeUi;
  name: string;
  profilePicture?: string;
  type: EAnimalTypesUI;
  specie?: TPredefinedOptionTypeUi;
  complete: boolean;
  // will be extended by response of animalsList api in Animals page
  gender?: EGenderUi;
  reproduction?: EReproductive | null;
  status?: EAnimalStatusesUi;
  weight?: TAnimalWeightProps;
  blockedActions?: TBlockedActionUI[];
  conversion?: TAnimalConversionToPetUI;
  verificationStatus?: EVerificationStatusUi;
  hasActiveResidency?: EAnimalStatusUponDeletionUI | null;
  species?: string;
  speciesPredefined?: boolean;
};

export type TResidencyAnimalDB = TAnimalApplicationDataDB & {
  animal: TResidencyAnimalInfoDB;
};

export type TResidencyWithAnimalsDataDB = {
  id: string;
  animalResidencyId: string;
  status: ETenantResidencyStatus;
  propertyManagementName: string;
  propertyManagementLogo: string;
  propertyName: string;
  unitNumber: string;
  moveInDate?: string;
  leaseToDate?: string;
  nextLeaseToDate?: string;
  animals: TResidencyAnimalDB[];
  roommateAnimalCount: number;
  statusReason?: string;
  active: boolean;

  propertyId: string;
  unitId: string | null;
  editAllowed: boolean;
};

// map TResidencyWithAnimalsDataDB to serve UI needs
export type TResidencyByApplicationDataUI = {
  residency: TResidencyInfoUI;
  application?: TAnimalApplicationDataUI;
  animal?: TResidencyAnimalInfoUI;
};

export type TResidencyInfoUI = {
  id: string;
  leaseToDate?: string;
  moveInDate?: string;
  nextLeaseToDate?: string;
  propertyManagementLogo: string;
  propertyManagementName: string;
  propertyName: string;
  roommateAnimalCount: number;
  status: ETenantResidencyStatusUI;
  statusReason?: string;
  unitNumber: string;
  active: boolean;
  propertyId: string;
  unitId: string | null;
  canEdit: boolean;
};
